import { z } from 'zod';
import { CardType, PaymentRail } from '../../../payment.types';
import { StoredMaskedPanSchema } from '../../masked_pan.schemas';
import {
    type CardPointeCardSecureBrowserTokenizationSession,
    CardPointeTokenizationResultType,
    CardPointeBrowserTokenizationSessionType,
    type CardPointeCardSecureBrowserTokenizationResult,
    type CardPointeCardSecureTokenizationSuccessResponse,
    type CardPointeCreditCardTokenizedCardMetadata,
    type CardPointeDebitCardTokenizedCardMetadata,
    type CardPointeStoredCardMetadataTokenizationResult,
    type CardPointeTokenizationResult,
    type CardPointeTokenizedCardMetadata,
} from '../../../types/ipg/cardpointe/cardpointe-tokenization.types';

export const CardPointeTokenizationTokenSchema: z.ZodString = z.string().min(1);

export const CardPointeCardSecureBrowserTokenizationSessionSchema:
    z.ZodObject<{
        browser_tokenization_session_type: z.ZodLiteral<
            typeof CardPointeBrowserTokenizationSessionType
        >;
        card_secure_base_url: z.ZodString;
    }> =
    z
        .object({
            browser_tokenization_session_type: z.literal(
                CardPointeBrowserTokenizationSessionType
            ),
            card_secure_base_url: z.string().url(),
        })
        .strict() satisfies z.ZodType<CardPointeCardSecureBrowserTokenizationSession>;

export const CardPointeCardSecureTokenizationSuccessResponseSchema: z.ZodType<CardPointeCardSecureTokenizationSuccessResponse> =
    z
        .object({
            message: z.literal('No error'),
            errorcode: z.literal(0),
            token: CardPointeTokenizationTokenSchema,
        })
        .strict();

export const CardPointeCreditCardTokenizedCardMetadataSchema: z.ZodObject<{
    payment_rail: z.ZodLiteral<typeof PaymentRail.CREDIT_CARD>;
    masked_pan: typeof StoredMaskedPanSchema;
    card_type: z.ZodLiteral<typeof CardType.CREDIT>;
    card_brand: z.ZodString;
    cardholder_name: z.ZodString;
    expiry_date: z.ZodOptional<z.ZodString>;
}> =
    z
        .object({
            payment_rail: z.literal(PaymentRail.CREDIT_CARD),
            masked_pan: StoredMaskedPanSchema,
            card_type: z.literal(CardType.CREDIT),
            card_brand: z.string().min(1),
            cardholder_name: z.string().min(1),
            expiry_date: z
                .string()
                .regex(/^\d{2}\/\d{2}$/)
                .optional(),
        })
        .strict() satisfies z.ZodType<CardPointeCreditCardTokenizedCardMetadata>;

export const CardPointeDebitCardTokenizedCardMetadataSchema: z.ZodObject<{
    payment_rail: z.ZodLiteral<typeof PaymentRail.DEBIT_CARD>;
    masked_pan: typeof StoredMaskedPanSchema;
    card_type: z.ZodLiteral<typeof CardType.DEBIT>;
    card_brand: z.ZodString;
    cardholder_name: z.ZodString;
    expiry_date: z.ZodOptional<z.ZodString>;
}> =
    z
        .object({
            payment_rail: z.literal(PaymentRail.DEBIT_CARD),
            masked_pan: StoredMaskedPanSchema,
            card_type: z.literal(CardType.DEBIT),
            card_brand: z.string().min(1),
            cardholder_name: z.string().min(1),
            expiry_date: z
                .string()
                .regex(/^\d{2}\/\d{2}$/)
                .optional(),
        })
        .strict() satisfies z.ZodType<CardPointeDebitCardTokenizedCardMetadata>;

export const CardPointeTokenizedCardMetadataSchema: z.ZodType<CardPointeTokenizedCardMetadata> =
    z.discriminatedUnion('payment_rail', [
        CardPointeCreditCardTokenizedCardMetadataSchema,
        CardPointeDebitCardTokenizedCardMetadataSchema,
    ]);

export const CardPointeCardSecureBrowserTokenizationResultSchema: z.ZodObject<{
    result_type: z.ZodLiteral<
        typeof CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION
    >;
    cardsecure_response: typeof CardPointeCardSecureTokenizationSuccessResponseSchema;
    card_metadata: z.ZodOptional<
        typeof CardPointeTokenizedCardMetadataSchema
    >;
}> =
    z
        .object({
            result_type: z.literal(
                CardPointeTokenizationResultType.CARDSECURE_BROWSER_TOKENIZATION
            ),
            cardsecure_response:
                CardPointeCardSecureTokenizationSuccessResponseSchema,
            card_metadata: CardPointeTokenizedCardMetadataSchema.optional(),
        })
        .strict() satisfies z.ZodType<CardPointeCardSecureBrowserTokenizationResult>;

export const CardPointeStoredCardMetadataTokenizationResultSchema: z.ZodObject<{
    result_type: z.ZodLiteral<
        typeof CardPointeTokenizationResultType.STORED_CARD_METADATA
    >;
    token: typeof CardPointeTokenizationTokenSchema;
    card_metadata: typeof CardPointeTokenizedCardMetadataSchema;
}> =
    z
        .object({
            result_type: z.literal(
                CardPointeTokenizationResultType.STORED_CARD_METADATA
            ),
            token: CardPointeTokenizationTokenSchema,
            card_metadata: CardPointeTokenizedCardMetadataSchema,
        })
        .strict() satisfies z.ZodType<CardPointeStoredCardMetadataTokenizationResult>;

export const CardPointeTokenizationResultSchema: z.ZodType<CardPointeTokenizationResult> =
    z.discriminatedUnion('result_type', [
        CardPointeCardSecureBrowserTokenizationResultSchema,
        CardPointeStoredCardMetadataTokenizationResultSchema,
    ]);
